import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';
import Badge from '../ui/Badge';
import LoadingSpinner from '../ui/LoadingSpinner';
import { FormatBrazilDateTimeShort } from '../../utils/dateUtils';

// Poll do badge a cada 60s: o worker de alertas roda em ticks de minutos, então
// polling mais agressivo só queimaria requests sem notificação nova pra mostrar.
const POLL_INTERVAL_MS = 60_000;
// Espelho do default do backend (repo clampa 1..100, default 30) — o dropdown
// é uma janela das últimas, não um histórico paginado.
const FEED_LIMIT = 30;

// severity do wire → variante semântica do Badge (tokens do tailwind.config.js).
// 'critical' vira 'danger' porque é o nome do token de cor no design system.
const SEVERITY_VARIANT = {
    info: 'info',
    warning: 'warning',
    critical: 'danger',
};

const SEVERITY_LABEL = {
    info: 'Info',
    warning: 'Atenção',
    critical: 'Crítico',
};

/**
 * Tempo relativo curto pt-BR ("agora", "há 5 min", "há 2 h", "há 3 d").
 * Local (não vive em utils/) porque o formato "curto de feed" só existe aqui;
 * acima de 7 dias delega pro formatador canônico de data do app — a idade
 * exata deixa de importar e a data absoluta comunica melhor.
 */
function formatRelativeTime(iso) {
    if (!iso) {
        return '';
    }
    const timestamp = new Date(iso).getTime();
    if (Number.isNaN(timestamp)) {
        return '';
    }
    const elapsedMs = Date.now() - timestamp;
    const minutes = Math.floor(elapsedMs / 60_000);
    if (minutes < 1) {
        return 'agora';
    }
    if (minutes < 60) {
        return `há ${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
        return `há ${hours} h`;
    }
    const days = Math.floor(hours / 24);
    if (days <= 7) {
        return `há ${days} d`;
    }
    return FormatBrazilDateTimeShort(iso);
}

// SVG inline: `shared/icons` (fonte única de ícones) ainda não exporta um sino
// e está fora do escopo desta fase — quando o Bell for promovido pra lá, este
// componente troca o SVG pelo import sem mudar mais nada.
const BellIcon = ({ size = 22 }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
);

/**
 * Sino de notificações in-app: badge com o total de não lidas + dropdown com
 * as últimas do feed. Se o usuário não tem a permissão do feed (v1 = alertas
 * de campanhas), não renderiza nada — o polling nem começa.
 */
/**
 * Para onde cada notificação leva. O feed já traz `item_id`/`campaign_id`
 * justamente para o deep-link não depender de parsear o título.
 *   · com anúncio  → PÁGINA do anúncio, aberta na aba Histórico de campanhas
 *   · só campanha  → lista de campanhas
 *   · sem nenhum   → auditoria de mudanças (o feed geral em forma de tabela)
 */
export function NotificationTarget(item) {
    if (item?.item_id) {
        return `/campaigns/items/${encodeURIComponent(item.item_id)}?tab=historico`;
    }
    if (item?.campaign_id) {
        return '/campaigns?tab=campaigns';
    }
    return '/campaigns?tab=audit-events';
}

const NotificationsBell = () => {
    const navigate = useNavigate();
    const { checkPermission } = useAuth();
    // Espelha a permissão da rota do backend (FEED_PERMISSION): sem ela o GET
    // /notifications/feed devolveria 403 e o polling viraria ruído de erro.
    const canSeeFeed = checkPermission('campaigns:read');

    const [isOpen, setIsOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const containerRef = useRef(null);

    const fetchUnreadCount = useCallback(async () => {
        if (!canSeeFeed) {
            return;
        }
        try {
            // limit=1: o badge só precisa do unread_count (que é do tenant
            // inteiro, independente da página) — não vale puxar o feed todo.
            const response = await api.getNotificationsFeed({ limit: 1 });
            setUnreadCount(response?.unread_count ?? 0);
        } catch {
            // Silencioso de propósito: falha de polling de badge não pode virar
            // erro recorrente na tela — o dropdown aberto tem tratamento próprio.
        }
    }, [canSeeFeed]);

    const fetchFeed = useCallback(async () => {
        if (!canSeeFeed) {
            return;
        }
        setIsLoading(true);
        setError(null);
        try {
            const response = await api.getNotificationsFeed({ limit: FEED_LIMIT });
            setItems(Array.isArray(response?.items) ? response.items : []);
            setUnreadCount(response?.unread_count ?? 0);
        } catch (err) {
            setError(err?.message || 'Não foi possível carregar as notificações.');
        } finally {
            setIsLoading(false);
        }
    }, [canSeeFeed]);

    // Badge: busca imediata + polling. O cleanup do interval é obrigatório —
    // sem ele cada remontagem do layout somaria um poll paralelo eterno.
    useEffect(() => {
        if (!canSeeFeed) {
            return undefined;
        }
        fetchUnreadCount();
        const intervalId = setInterval(fetchUnreadCount, POLL_INTERVAL_MS);
        return () => clearInterval(intervalId);
    }, [canSeeFeed, fetchUnreadCount]);

    // Abrir = refetch do feed: o dropdown mostra o estado de AGORA, não o da
    // última abertura (o polling só atualiza o contador, não a lista).
    useEffect(() => {
        if (isOpen) {
            fetchFeed();
        }
    }, [isOpen, fetchFeed]);

    // Fechar por clique fora / Escape — só escuta enquanto aberto.
    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }
        const handleMouseDown = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleMouseDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleMouseDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen]);

    // Clicar = marcar lida E ir para o assunto. Antes só marcava lida, e uma
    // notificação já lida não fazia nada — o dono clicava e "não acontecia nada".
    const handleItemClick = (item) => {
        if (!item.read_at) {
            // Otimista: marca local primeiro (feedback imediato); se o POST falhar,
            // o refetch ressincroniza com a verdade do servidor.
            setItems((prev) =>
                prev.map((row) => (row.id === item.id ? { ...row, read_at: new Date().toISOString() } : row)),
            );
            setUnreadCount((count) => Math.max(0, count - 1));
            api.markNotificationsRead([item.id]).catch(() => fetchFeed());
        }
        setIsOpen(false);
        navigate(NotificationTarget(item));
    };

    const handleMarkAllRead = () => {
        const stamp = new Date().toISOString();
        setItems((prev) => prev.map((row) => (row.read_at ? row : { ...row, read_at: stamp })));
        setUnreadCount(0);
        api.markAllNotificationsRead().catch(() => fetchFeed());
    };

    if (!canSeeFeed) {
        return null;
    }

    return (
        <div ref={containerRef} className="relative">
            <button
                type="button"
                onClick={() => setIsOpen((open) => !open)}
                title="Notificações"
                aria-label={unreadCount > 0 ? `Notificações: ${unreadCount} não lidas` : 'Notificações'}
                className="relative flex h-10 w-10 items-center justify-center rounded-full border border-mono-200 bg-white text-mono-600 shadow-mono transition-colors duration-200 hover:bg-mono-50 hover:text-black dark:border-mono-800 dark:bg-mono-950 dark:text-mono-400 dark:hover:bg-mono-900 dark:hover:text-white"
            >
                <BellIcon />
                {unreadCount > 0 && (
                    <span className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-danger-600 px-1 text-[10px] font-semibold leading-none text-white">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {isOpen && (
                <div className="absolute right-0 z-50 mt-2 w-96 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-mono-200 bg-white shadow-2xl dark:border-mono-800 dark:bg-mono-950">
                    <div className="flex items-center justify-between border-b border-mono-200 px-4 py-3 dark:border-mono-800">
                        <p className="text-sm font-semibold text-black dark:text-white">Notificações</p>
                        <button
                            type="button"
                            onClick={handleMarkAllRead}
                            disabled={unreadCount === 0}
                            className="text-xs font-medium text-mono-600 transition-colors hover:text-black disabled:cursor-default disabled:opacity-40 dark:text-mono-400 dark:hover:text-white"
                        >
                            Marcar todas como lidas
                        </button>
                    </div>

                    <div className="max-h-96 overflow-y-auto overscroll-contain">
                        {isLoading ? (
                            <LoadingSpinner className="py-10" />
                        ) : error ? (
                            <div className="space-y-2 px-4 py-8 text-center">
                                <p className="text-sm text-mono-600 dark:text-mono-400">{error}</p>
                                <button
                                    type="button"
                                    onClick={fetchFeed}
                                    className="text-xs font-medium text-black underline dark:text-white"
                                >
                                    Tentar novamente
                                </button>
                            </div>
                        ) : items.length === 0 ? (
                            <p className="px-4 py-8 text-center text-sm text-mono-500">
                                Nenhuma notificação por aqui.
                            </p>
                        ) : (
                            items.map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => handleItemClick(item)}
                                    className={`block w-full border-b border-mono-100 px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-mono-50 dark:border-mono-900 dark:hover:bg-mono-900 ${
                                        item.read_at ? 'opacity-60' : ''
                                    }`}
                                >
                                    <div className="flex items-start gap-2">
                                        {/* Ponto de não lida ocupa o slot mesmo lida (invisible,
                                            não hidden) pra alinhar os títulos entre linhas. */}
                                        <span
                                            className={`mt-1.5 h-2 w-2 shrink-0 rounded-full bg-info-500 ${
                                                item.read_at ? 'invisible' : ''
                                            }`}
                                            aria-hidden="true"
                                        />
                                        <div className="min-w-0 flex-1 space-y-1">
                                            <p className="text-sm font-medium text-black dark:text-white">
                                                {item.title}
                                            </p>
                                            {item.body && (
                                                <p className="line-clamp-2 text-xs text-mono-600 dark:text-mono-400">
                                                    {item.body}
                                                </p>
                                            )}
                                            <div className="flex items-center gap-2">
                                                <Badge variant={SEVERITY_VARIANT[item.severity] || 'default'}>
                                                    {SEVERITY_LABEL[item.severity] || item.severity}
                                                </Badge>
                                                <span
                                                    className="text-[11px] text-mono-500"
                                                    title={FormatBrazilDateTimeShort(item.created_at)}
                                                >
                                                    {formatRelativeTime(item.created_at)}
                                                </span>
                                                {item.item_id ? (
                                                    <span className="ml-auto font-mono text-[10px] text-info-600 dark:text-info-400">
                                                        abrir {item.item_id} →
                                                    </span>
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationsBell;
